import { useState, type ReactNode } from "react";
import { CaretDown, CaretUp, Users, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { avatarColor, initials } from "@/lib/avatar";
import type { Participant } from "@/lib/api";
import styles from "./ParticipantsBar.module.css";

const COMPACT_LIMIT = 6;

type Props = {
  participants: Participant[];
  /** Beim Aufklappen wird dieser Slot gerendert (volle Liste mit Cards). */
  detailsSlot: ReactNode;
  /** Optional zusätzlicher Inhalt rechts in der Compact-Bar (z. B. „Add"-Button bei Admin). */
  rightSlot?: ReactNode;
  /** ID des Self-Participants (User-Rolle), wird im Compact-Avatar markiert. */
  selfId?: number;
};

export function ParticipantsBar({ participants, detailsSlot, rightSlot, selfId }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const attending = participants.filter((p) => p.participates);
  const declining = participants.filter((p) => !p.participates);
  const totalPersons = attending.reduce((sum, p) => sum + (p.persons || 0), 0);

  const visibleAvatars = participants.slice(0, COMPACT_LIMIT);
  const overflow = participants.length - COMPACT_LIMIT;

  return (
    <section className={styles.bar} aria-label={t("event.participantsHeading")}>
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <Users size={18} weight="regular" className={styles.headIcon} />
          <div className={styles.summary}>
            <span className={styles.summaryMain}>
              {participants.length === 0
                ? t("event.noParticipantsYet")
                : t("event.attendingCount", {
                    count: attending.length,
                    persons: totalPersons,
                  })}
            </span>
            {declining.length > 0 && (
              <span className={styles.summarySub}>
                {t("event.decliningCount", { count: declining.length })}
              </span>
            )}
          </div>
        </div>
        <div className={styles.headRight}>
          {rightSlot}
          {participants.length > 0 && (
            <button
              type="button"
              className={styles.toggleButton}
              aria-expanded={open}
              aria-controls="participants-details"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? (
                <>
                  {t("event.hideDetails")}
                  <CaretUp size={14} weight="bold" />
                </>
              ) : (
                <>
                  {t("event.showDetails")}
                  <CaretDown size={14} weight="bold" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {participants.length > 0 && (
        <div className={styles.avatars}>
          {visibleAvatars.map((p) => {
            const isSelf = selfId === p.id;
            const declined = !p.participates;
            return (
              <span
                key={p.id}
                className={`${styles.avatar} ${declined ? styles.avatarDeclined : ""} ${isSelf ? styles.avatarSelf : ""}`}
                style={{ backgroundColor: avatarColor(p.name) }}
                title={p.name}
                aria-label={p.name}
              >
                {initials(p.name)}
                {declined && (
                  <span className={styles.avatarBadge}>
                    <X size={8} weight="bold" />
                  </span>
                )}
              </span>
            );
          })}
          {overflow > 0 && (
            <span className={styles.avatarMore} aria-hidden>
              +{overflow}
            </span>
          )}
        </div>
      )}

      {open && (
        <div id="participants-details" className={styles.details}>
          {detailsSlot}
        </div>
      )}
    </section>
  );
}
